/**
 * Single source of truth for facts that were previously hardcoded and
 * repeated (with drift) across multiple pages, `index.html`, and
 * `llms.txt`. See docs/engineering/backlog/AEO-SEO-DEEP-REVIEW-2026-09.md
 * Finding 1. Import these instead of retyping the underlying facts.
 */

export const COMPANY_NAME = "Texas First Response Services";
export const COMPANY_ALT_NAME = "TFRS";
export const SITE_URL = "https://tx1rs.com";
export const PHONE_DISPLAY = "(832) 225-2838";
export const PHONE_E164 = "+1-832-225-2838";
/**
 * Dedicated SMS line — a separate number from PHONE_DISPLAY/PHONE_E164
 * above, meant to open a new text thread (an `sms:` link), not a call.
 * Keep the two numbers distinct; don't collapse them into one "contact
 * number" constant.
 */
export const SMS_PHONE_DISPLAY = "(281) 843-3528";
export const SMS_URI = "sms:+12818433528";
/**
 * Checkbox copy shown under the phone field on every lead-capture form that
 * collects one -- transactional consent only (account, order/build status,
 * and quote/appointment updates), never bundled with marketing consent.
 * Deliberately framed as granting permission ("I agree to let...") rather
 * than requesting a service, and not scoped to "this inquiry" specifically
 * -- per the maintainer's 2026-10-19 direction, the earlier inquiry-scoped
 * "send me updates about this" phrasing read as the visitor asking us for
 * something, when the point is us asking them for permission to text.
 * Mirrors Privacy.tsx's "SMS / Text Messaging" section (rates/frequency
 * disclosure, STOP/HELP) -- keep both in sync if either changes. Unchecked
 * by default on every form; checking it is what causes server/highlevel.ts
 * to add the `consent:sms` GHL tag.
 */
export const SMS_CONSENT_LABEL =
  "I agree to let Texas First Response Services send me text messages about my account, orders, and service updates. Msg & data rates may apply, frequency varies. Reply STOP to opt out, HELP for help.";
export const EMAIL = "info@tx1rs.com";
export const ADDRESS = {
  streetAddress: "7727 Wetzel Rd",
  addressLocality: "Dickinson",
  addressRegion: "TX",
  postalCode: "77539",
  addressCountry: "US",
};

export interface SbaCertification {
  code: string;
  name: string;
}

/** The authoritative list — 6 certifications. Any page/stat citing a different count is wrong. */
export const SBA_CERTIFICATIONS: SbaCertification[] = [
  { code: "WOSB", name: "Woman-Owned Small Business" },
  { code: "EDWOSB", name: "Economically Disadvantaged Woman-Owned Small Business" },
  { code: "SDVOSB", name: "Service-Disabled Veteran-Owned Small Business" },
  { code: "VOSB", name: "Veteran-Owned Small Business" },
  { code: "HUBZone", name: "Historically Underutilized Business Zone" },
  { code: "8(a)", name: "SBA 8(a) Business Development Program" },
];

export const SBA_CERTIFICATION_COUNT = SBA_CERTIFICATIONS.length;

/**
 * Verbatim canonical "who we are" line — repeat this exactly, don't
 * paraphrase it. The certification list is derived from SBA_CERTIFICATIONS
 * above so this can't silently drift back to an incomplete subset (see
 * docs/engineering/backlog/GH-166 — a prior version of this string, plus
 * index.html's and seoMetadata.ts's homepage description, all listed only
 * 3 of the 6 certifications).
 */
export const CANONICAL_DESCRIPTION =
  `SBA 8(a) certified emergency vehicle upfitter serving law enforcement, fire/EMS, K9, and commercial fleets across Texas. Holds all six SBA certifications: ${SBA_CERTIFICATIONS.map(c => c.code).join(", ")}.`;

/**
 * TFRS-performed installations (the vast majority of the catalog) carry a
 * lifetime workmanship warranty. The 2027 Crew Cab Brush Truck is a
 * different case: it's manufactured by Redline, with TFRS acting as the
 * authorized dealer, so it carries Redline's own 3-year labor warranty
 * instead — this is not a contradiction to resolve to one value, it's two
 * true facts about two different kinds of builds.
 */
export const WARRANTY_LIFETIME_INSTALLS = "lifetime workmanship warranty";
export const WARRANTY_CREW_CAB_BRUSH_TRUCK = {
  term: "3-year labor warranty",
  note: "The 2027 Crew Cab Brush Truck is manufactured by Redline; Texas First Response Services is the authorized dealer, so it carries Redline's 3-year labor warranty rather than TFRS's own lifetime workmanship warranty on installs it performs directly.",
};

/**
 * Cooperative purchasing contract vehicle. Replaced HGAC (Houston-Galveston
 * Area Council) with TIPS USA per the maintainer's 2026-09-15 directive —
 * TIPS is now the operative contract for procurement purposes, so every
 * public/electronic reference must cite it instead of HGAC.
 */
export const TIPS_CONTRACT_NUMBER = "260404";
export const TIPS_ORGANIZATION = "The Interlocal Purchasing System (TIPS)";
export const TIPS_SHORT_NAME = "TIPS USA";
